import { forwardRef, useContext, useEffect, useRef } from 'react'
import {
  TooltipContentContextProvider,
  TooltipContextProvider
} from './tooltip-context'
import {
  TOOLTIP_OPEN,
  tooltipAlignOptions,
  tooltipColorOptions,
  tooltipSideOptions,
  tooltipSizeOptions
} from '../constants'
import {
  StyledContentChildren,
  StyledTooltipContent
} from '../styled-components'
import { useComposedRefs } from '../../../../../utils/customHooks'
import PropTypes from 'prop-types'

const TooltipContent = forwardRef((props, forwardedRef) => {
  const { children, onEscapeKeyDown, onPointerDownOutside, ...contentProps } =
    props

  const context = useContext(TooltipContextProvider)

  const ref = useRef(null)
  const composedRefs = useComposedRefs(forwardedRef, ref)

  const { onClose } = context

  // Close this tooltip if another one opens
  useEffect(() => {
    document.addEventListener(TOOLTIP_OPEN, onClose)
    return () => document.removeEventListener(TOOLTIP_OPEN, onClose)
  }, [onClose])

  // Close the tooltip if the trigger is scrolled
  useEffect(() => {
    if (context.trigger) {
      const handleScroll = (event) => {
        const target = event.target
        if (target?.contains(context.trigger)) onClose()
      }
      window.addEventListener('scroll', handleScroll, { capture: true })
      return () =>
        window.removeEventListener('scroll', handleScroll, { capture: true })
    }
  }, [context.trigger, onClose])

  if (!context.open) {
    return null
  }

  return (
    <TooltipContentContextProvider.Provider value={contentProps}>
      <StyledTooltipContent
        data-state={context.stateAttribute}
        {...contentProps}
        ref={composedRefs}
        style={{
          ...contentProps.style

          // re-namespace exposed content custom properties
        }}
        aria-label="tooltip-content"
      >
        <StyledContentChildren>{children}</StyledContentChildren>
      </StyledTooltipContent>
    </TooltipContentContextProvider.Provider>
  )
})

TooltipContent.displayName = 'TooltipContent'

TooltipContent.propTypes = {
  align: PropTypes.oneOf(Object.values(tooltipAlignOptions)),
  color: PropTypes.oneOf(Object.values(tooltipColorOptions)),
  side: PropTypes.oneOf(Object.values(tooltipSideOptions)),
  size: PropTypes.oneOf(Object.values(tooltipSizeOptions)),
  sideOffset: PropTypes.number
}

export default TooltipContent
